/**
 * Formats a phone number for display purposes
 * Format: {country code} {3 digits} {3 digits} {4 digits}
 * Example: +1 415 555 2671
 *
 * @param phoneNumber - The phone number string to format (can include non-numeric characters)
 * @param options - Optional configuration for formatting
 * @returns Formatted phone number string or empty string if invalid
 */

interface FormatPhoneNumberOptions {
  /** Country code (e.g., '+1', '+44'). If not provided, assumes it's in the input */
  countryCode?: string;
  /** Separator between segments (default: space) */
  separator?: string;
}

export default function formatPhoneNumber(
  phoneNumber: string | null | undefined,
  options: FormatPhoneNumberOptions = {}
): string {
  if (!phoneNumber) {
    return '';
  }

  const { separator = ' ' } = options;

  // Remove all non-numeric characters except the leading '+' sign
  let cleaned = phoneNumber.toString().trim();
  const hasPlus = cleaned.startsWith('+');

  // Extract only digits
  const digits = cleaned.replace(/\D/g, '');

  if (!digits) {
    return '';
  }

  // Apply country code if provided in options
  let countryCode = options.countryCode || '';
  let remainingDigits = digits;

  // If phone number starts with '+', extract country code from the number
  if (hasPlus && !options.countryCode) {
    // Check if it starts with common country codes
    if (digits.startsWith('1') && digits.length >= 11) {
      // US/Canada (+1) - country code is 1 digit
      countryCode = '+1';
      remainingDigits = digits.substring(1);
    } else if (digits.length >= 10) {
      // For other countries, determine country code length
      // Standard assumption: if total length > 10, first 1-3 digits are country code
      if (digits.length === 11 && digits.startsWith('1')) {
        countryCode = '+1';
        remainingDigits = digits.substring(1);
      } else if (digits.length >= 12) {
        // 2-3 digit country code
        const countryCodeLength = Math.min(3, digits.length - 10);
        countryCode = '+' + digits.substring(0, countryCodeLength);
        remainingDigits = digits.substring(countryCodeLength);
      } else {
        // Default to 1-digit country code for 11 digits
        countryCode = '+' + digits.substring(0, 1);
        remainingDigits = digits.substring(1);
      }
    } else {
      // Less than 10 digits with +, assume US
      countryCode = '+1';
      remainingDigits = digits;
    }
  } else if (options.countryCode) {
    // Use provided country code
    if (!countryCode.startsWith('+')) {
      countryCode = '+' + countryCode;
    }
  } else {
    // No + sign and no explicit country code
    if (digits.length >= 11) {
      // Assume 1-digit country code for 11 digits
      countryCode = '+' + digits.substring(0, 1);
      remainingDigits = digits.substring(1);
    } else if (digits.length === 10) {
      // Assume US/Canada
      countryCode = '+1';
      remainingDigits = digits;
    } else {
      // Less than 10 digits, assume US
      countryCode = '+1';
      remainingDigits = digits;
    }
  }

  // Ensure we have at least 10 digits for the main number
  if (remainingDigits.length < 10) {
    // Return partially formatted if less than 10 digits
    if (remainingDigits.length <= 3) {
      return countryCode + separator + remainingDigits;
    } else if (remainingDigits.length <= 6) {
      return (
        countryCode +
        separator +
        remainingDigits.substring(0, 3) +
        separator +
        remainingDigits.substring(3)
      );
    } else {
      return (
        countryCode +
        separator +
        remainingDigits.substring(0, 3) +
        separator +
        remainingDigits.substring(3, 6) +
        separator +
        remainingDigits.substring(6)
      );
    }
  }

  // Format as: {country code} {3 digits} {3 digits} {4 digits}
  const part1 = remainingDigits.substring(0, 3); // First 3 digits
  const part2 = remainingDigits.substring(3, 6); // Next 3 digits
  const part3 = remainingDigits.substring(6, 10); // Last 4 digits
  const remaining = remainingDigits.substring(10); // Any extra digits

  let formatted = countryCode + separator + part1 + separator + part2 + separator + part3;

  // Append any remaining digits
  if (remaining) {
    formatted += separator + remaining;
  }

  return formatted.trim();
}

/**
 * Validates if a phone number string is valid
 * @param phoneNumber - The phone number to validate
 * @returns true if valid, false otherwise
 */
export function isValidPhoneNumber(phoneNumber: string | null | undefined): boolean {
  if (!phoneNumber) {
    return false;
  }

  const digits = phoneNumber.replace(/\D/g, '');
  // Valid phone number should have at least 10 digits
  return digits.length >= 10;
}

/**
 * Extracts digits only from a phone number string
 * @param phoneNumber - The phone number string
 * @returns String of digits only
 */
export function getPhoneNumberDigits(phoneNumber: string | null | undefined): string {
  if (!phoneNumber) {
    return '';
  }

  return phoneNumber.replace(/\D/g, '');
}
